import { Box, Typography, Alert } from '@mui/material';
import SearchBar from '../../../components/SearchBar';
import AssignNewModemTable from './AssignNewModemTable';
import { Button } from '../../../components/shared';
import useStyles from './style';
import { useState, useEffect } from 'react';
import {
  useUpdateModemByIdMutation,
  useDeleteModemByIdMutation,
  useLazyGetModemResyncStatusQuery,
} from '../../../services/Modems/ModemsService';
import { useLazyGetAssetResyncStatusQuery } from '../../../services/Machines/MachinesService';
import { isArrayWithLength, dateFormatForApi } from '../../../utils/common-methods';
import ConfirmationModal from '../../../components/ConfirmationModel';
import TimerModal from '../../../components/TimerModal';
import InPageNotification from '../../../components/InPageNotification';
import { CheckCircle } from '@mui/icons-material';
import { IN_PAGE_NOTIFICATION_TYPES } from '../../../constants/InPageNotificationTypes';
import { useSelector } from 'react-redux';

const AssignModem = ({ machineDetails, showAssignModem, modemInfo = [], refetachMachine }) => {
  const classes = useStyles();
  const [rows, setRows] = useState({});
  const [searchText, setSearchText] = useState('');
  const [updateModemApi] = useUpdateModemByIdMutation();
  const [deleteModemApi] = useDeleteModemByIdMutation();
  const [getAssetResyncStatus] = useLazyGetAssetResyncStatusQuery();
  const [getModemResyncStatus] = useLazyGetModemResyncStatusQuery();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccessModemDelete, setShowSuccessModemDelete] = useState(false);
  const [deleteModemId, setDeleteModemId] = useState(null);
  const [inEffectFromDates, setInEffectFromDates] = useState({});
  const [validationError, setValidationError] = useState('');
  const [resyncWarning, setResyncWarning] = useState('');
  const [notification, setNotification] = useState(null);
  const isResyncEnabled = process.env.REACT_APP_ENABLE_FEED_RESYNC === 'true';
  const updatedBy = useSelector((state) => state.user?.userDetails?.id);

  // For override confirmation
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [apiError, setApiError] = useState('');

  useEffect(() => {
    if (isArrayWithLength(modemInfo)) {
      let preSelectedRow = {};
      modemInfo?.forEach((modem) => {
        preSelectedRow = { ...preSelectedRow, [modem?.id || modem?.modemId]: true };
      });
      setRows({ ...preSelectedRow });
    }
  }, [modemInfo]);

  // Only allow one modem to be selected at a time
  const onSelect = (row, checked) => {
    if (checked) {
      setRows({ [row.id]: true });
    } else {
      setRows({});
    }
    if (!checked) {
      setInEffectFromDates((prev) => {
        const newDates = { ...prev };
        delete newDates[row.id];
        return newDates;
      });
    }
  };

  const onDateChange = (id, date) => {
    setInEffectFromDates((prev) => ({
      ...prev,
      [id]: date,
    }));
  };

  const onDeleteModem = (row) => {
    setDeleteModemId(row.id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModemId) return;
    try {
      await deleteModemApi(deleteModemId).unwrap();
      setShowDeleteModal(false);
      setShowSuccessModemDelete(true);
      setDeleteModemId(null);
      setRows({});
      setInEffectFromDates({});
      setNotification({
        type: IN_PAGE_NOTIFICATION_TYPES.SUCCESS,
        message: 'Modem deleted successfully.',
        icon: <CheckCircle color="success" />,
      });
    } catch (error) {
      setShowDeleteModal(false);
      setDeleteModemId(null);
      setNotification({
        type: IN_PAGE_NOTIFICATION_TYPES.ERROR,
        message: 'Failed to delete modem.',
      });
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeleteModemId(null);
  };

  // Assign handler with override logic
  const handleAssign = async () => {
    setValidationError('');
    setResyncWarning('');
    setApiError('');
    const selectedId = Object.keys(rows)[0];
    if (!selectedId) {
      setValidationError('Please select a modem.');
      return;
    }
    if (!inEffectFromDates[selectedId]) {
      setValidationError('Please select a modem.');
      return;
    }

    if (isResyncEnabled) {
      // Check resync status of the machine/asset before assigning
      if (machineDetails?.id) {
        try {
          const resyncResult = await getAssetResyncStatus(machineDetails.id).unwrap();
          if (resyncResult?.data?.resync) {
            setResyncWarning(
              'A resync is currently in progress for this asset. Please try again after resync is complete.',
            );
            return;
          }
        } catch {
          // If API fails, proceed
        }
      }

      // Check resync status of the selected modem
      if (selectedId) {
        try {
          const modemResyncResult = await getModemResyncStatus(selectedId).unwrap();
          if (modemResyncResult?.data?.resync) {
            setResyncWarning(
              'A resync is currently in progress for this modem. Please try again after resync is complete.',
            );
            return;
          }
        } catch {
          // If API fails, proceed
        }
      }
    }
    const payload = {
      id: selectedId,
      inEffectFrom: dateFormatForApi(inEffectFromDates[selectedId]),
      updatedBy,
      assetId: machineDetails.id,
      override: false,
    };
    try {
      await updateModemApi(payload).unwrap();
      setNotification({
        type: IN_PAGE_NOTIFICATION_TYPES.SUCCESS,
        message: 'Modem assigned successfully.',
        icon: <CheckCircle color="success" />,
      });
      showAssignModem(false);
      refetachMachine();
    } catch (error) {
      if (error.status === 409) {
        setApiError(error.data?.message || 'A conflict occurred while saving the record.');
        setShowOverrideModal(true);
        setPendingFormData({ ...payload, override: true });
      } else {
        setValidationError('Failed to assign modem.');
      }
    }
  };

  const handleOverrideConfirm = async () => {
    setShowOverrideModal(false);
    if (!pendingFormData) return;
    try {
      await updateModemApi(pendingFormData).unwrap();
      setNotification({
        type: IN_PAGE_NOTIFICATION_TYPES.SUCCESS,
        message: 'Modem assigned successfully (override).',
        icon: <CheckCircle color="success" />,
      });
      showAssignModem(false);
      setPendingFormData(null);
      setApiError('');
    } catch {
      setValidationError('Failed to override and assign modem.');
    }
  };

  const handleOverrideCancel = () => {
    setShowOverrideModal(false);
    setPendingFormData(null);
    setApiError('');
  };

  // Search handler for SearchBar
  const handleSearch = (value) => {
    setSearchText(value);
  };

  return (
    <Box className={classes.assignModemWrapper} sx={{ pb: 2 }}>
      <Typography variant="h2" gutterBottom>
        Assign Modem
      </Typography>
      <Box sx={{ mb: 3.5, mt: 2 }}>
        <SearchBar
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onSearch={handleSearch}
          placeholder="Search modem..."
          sx={{ mb: 4 }}
        />
      </Box>
      <AssignNewModemTable
        onSelect={onSelect}
        q={searchText}
        selectedRows={rows}
        onDeleteModem={onDeleteModem}
        onDateChange={onDateChange}
        inEffectFromDates={inEffectFromDates}
        assetId={machineDetails?.id}
      />
      {resyncWarning && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="warning">{resyncWarning}</Alert>
        </Box>
      )}
      {validationError && (
        <Typography color="error" sx={{ mt: 2 }}>
          {validationError}
        </Typography>
      )}
      <Box sx={{ mt: 5, mb: 1, display: 'flex', gap: 2 }}>
        <Button variant="contained" color="primary" onClick={handleAssign}>
          Assign
        </Button>
        <Button variant="outlined" onClick={() => showAssignModem(false)}>
          Cancel
        </Button>
      </Box>
      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Delete Modem"
        msg="Are you sure you want to delete this modem?"
        buttons={[
          { text: 'Confirm', value: 'confirm' },
          { text: 'Cancel', value: 'cancel' },
        ]}
        onClick={(val) => {
          if (val === 'confirm') {
            handleDeleteConfirm();
          } else {
            handleDeleteCancel();
          }
        }}
      />
      <ConfirmationModal
        isOpen={showOverrideModal}
        title="Update Confirmation"
        msg={apiError || 'There is a conflict with the modem data. Do you want to override and continue?'}
        buttons={[
          { text: 'Confirm', value: 'confirm' },
          { text: 'Cancel', value: 'cancel' },
        ]}
        onClick={(val) => {
          if (val === 'confirm') {
            handleOverrideConfirm();
          } else {
            handleOverrideCancel();
          }
        }}
      />
      <TimerModal
        open={showSuccessModemDelete}
        handleClose={() => setShowSuccessModemDelete(false)}
        message="Modem deleted successfully."
        timer={2000}
      />
      {notification && (
        <InPageNotification
          open={!!notification}
          type={notification.type}
          message={notification.message}
          icon={notification.icon}
          onClose={() => setNotification(null)}
        />
      )}
    </Box>
  );
};

export default AssignModem;
